import React, { useEffect, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { CardBrandType } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import {
  CreditCardClearanceReportEntity,
  CreditCardClearanceStatus,
  CreditCardClearanceType,
} from '../../../../api/types/payment-gateway/credit-card-clearance-entity';
import { formatDate } from '../../../../utils/date-format';
import { CreditCardClarenceFilter } from './components/credit-card-clarence-filter';
import {
  CreditCardClarenceFilterType,
  defaultCreditCardClarenceFilter,
} from './credit-card-clarence-filter.type';
import { getCreditCardClarenceListOperation } from './operations/get-credit-card-clarence-list-operation';

const cardBrandLabel = (brand: CardBrandType | null): string => {
  switch (brand) {
    case CardBrandType.VISA:
      return 'Visa';
    case CardBrandType.MASTERCARD:
      return 'Mastercard';
    case CardBrandType.ISRACARD:
      return 'Isracard';
    case CardBrandType.AMERICAN_EXPRESS:
      return 'American Express';
    case CardBrandType.DINERS_CLUB:
      return 'Diners Club';
    case CardBrandType.DISCOVER:
      return 'Discover';
    case CardBrandType.JCB:
      return 'JCB';
    case CardBrandType.MAESTRO:
      return 'Maestro';
    case CardBrandType.PRIVATE_LABEL:
      return 'כרטיס פרטי';
    default:
      return 'חסר';
  }
};

const clearanceStatusLabel = (status: CreditCardClearanceStatus): string => {
  switch (status) {
    case CreditCardClearanceStatus.SUCCESS:
      return 'הצליח';
    case CreditCardClearanceStatus.FAILED:
      return 'נכשל';
    case CreditCardClearanceStatus.PENDING:
      return 'ממתין';
    default:
      return status;
  }
};

const clearanceStatusSeverity = (
  status: CreditCardClearanceStatus,
): 'success' | 'warning' | 'danger' => {
  switch (status) {
    case CreditCardClearanceStatus.SUCCESS:
      return 'success';
    case CreditCardClearanceStatus.PENDING:
      return 'warning';
    case CreditCardClearanceStatus.FAILED:
      return 'danger';
    default:
      return 'warning';
  }
};

const clearanceTypeLabel = (type: CreditCardClearanceType): string => {
  switch (type) {
    case CreditCardClearanceType.REGULAR:
      return 'רגיל';
    case CreditCardClearanceType.INSTALLMENT:
      return 'תשלומים';
    default:
      return type;
  }
};

export const CreditCardClarencePage = () => {
  const [data, setData] = useState<CreditCardClearanceReportEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<CreditCardClarenceFilterType>(
    defaultCreditCardClarenceFilter(),
  );
  const [appliedFilters, setAppliedFilters] =
    useState<CreditCardClarenceFilterType>(filters);

  // Monotonic request id to discard out-of-order responses when the user
  // changes a filter / paginates while a previous request is still in flight.
  const searchSeqRef = useRef(0);

  const runSearch = async (
    nextFilters: CreditCardClarenceFilterType,
    nextOffset: number,
    nextLimit: number,
  ) => {
    const seq = ++searchSeqRef.current;
    const isCurrent = () => seq === searchSeqRef.current;

    await getCreditCardClarenceListOperation(
      nextFilters,
      nextOffset,
      nextLimit,
      (next) => {
        if (isCurrent()) setData(next);
      },
      (next) => {
        if (isCurrent()) setTotal(next);
      },
      (next) => {
        if (isCurrent()) setLoading(next);
      },
    );
  };

  // Initial load: fetch with the default filters when the page mounts.
  useEffect(() => {
    runSearch(appliedFilters, offset, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    setAppliedFilters(filters);
    setOffset(0);
    setData([]);
    setTotal(0);
    await runSearch(filters, 0, limit);
  };

  const handleClear = () => {
    setFilters(defaultCreditCardClarenceFilter());
  };

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await runSearch(appliedFilters, newOffset, newLimit);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        direction: 'rtl',
      }}
    >
      <CreditCardClarenceFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            fontSize: '14px',
            color: '#4b5563',
          }}
        >
          <span style={{ textAlign: 'right' }}>
            סה״כ רשומות: {total.toLocaleString()}
          </span>
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="id"
          tableStyle={{ direction: 'rtl' }}
        >
          <Column
            field="docNumber"
            header="מספר מסמך"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.docNumber ?? '—'
            }
          />
          <Column
            field="creditCardClearanceStatus"
            header="סטטוס סליקה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) => (
              <Tag
                value={clearanceStatusLabel(row.creditCardClearanceStatus)}
                severity={clearanceStatusSeverity(
                  row.creditCardClearanceStatus,
                )}
              />
            )}
          />
          <Column
            field="creditCardClearanceType"
            header="סוג סליקה"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              clearanceTypeLabel(row.creditCardClearanceType)
            }
          />
          <Column
            field="amount"
            header="סכום"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) => {
              if (typeof row.amount !== 'number') return 'חסר';
              const abs = Math.abs(row.amount).toFixed(2);
              return row.amount < 0 ? `${abs}-` : abs;
            }}
          />
          <Column
            field="currency"
            header="מטבע"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.currency ?? 'חסר'
            }
          />
          <Column
            header="כרטיס אשראי"
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) => {
              const brand = cardBrandLabel(row.cardBrand);
              const last4 = row.cardLast4?.trim() || '';
              return [brand, last4].filter(Boolean).join(' ');
            }}
          />
          <Column
            field="statusCode"
            header="קוד סטטוס"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.statusCode?.trim() ? row.statusCode : 'חסר'
            }
          />
          <Column
            field="approvalNumber"
            header="מספר אישור"
            style={{ textAlign: 'right', width: '130px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.approvalNumber?.trim() ? row.approvalNumber : 'חסר'
            }
          />
          <Column
            field="transactionId"
            header="מזהה עסקה"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.transactionId?.trim() ? row.transactionId : 'חסר'
            }
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
          <Column
            header="קובץ"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: CreditCardClearanceReportEntity) =>
              row.fileUrl ? (
                <a
                  href={row.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563eb',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <i className="pi pi-external-link" />
                  פתח
                </a>
              ) : (
                <span style={{ color: '#9ca3af' }}>חסר</span>
              )
            }
          />
        </DataTable>
      </div>
    </div>
  );
};
